<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { useUploadsStore } from '~/upload'
import { useI18n } from 'vue-i18n'
import { useStore } from '~/store'
import { useRoute } from 'vue-router'
import { to, dialog } from '~/routing'
import useMarkdown from '~/composables/useMarkdown'

import Logo from '~/components/Logo.vue'
import UserMenu from './UserMenu.vue'

import Badge from '~/ui/Badge.vue'
import Link from '~/ui/Link.vue'
import Popover from '~/ui/Popover.vue'
import PopoverItem from '~/ui/popover/PopoverItem.vue'
import Button from '~/ui/Button.vue'
import Layout from '~/ui/Layout.vue'
import Spacer from '~/ui/Spacer.vue'
import SearchModal from '~/shell/Search.vue'
import TermsModal from '~/gdpr/Terms.vue'

const isCollapsed = ref(true)

const route = useRoute()
watch(() => route.path, () => isCollapsed.value = true)

// Some sections (Artists, Albums, Playlists, Radios) have detail/sub-pages that are
// sibling routes, not children, of their `.browse` list route — so vue-router's
// own exact-active matching can't mark the nav link as current there. We match
// on the route name prefix instead.
const isSectionActive = (prefix: string) => route.name?.toString().startsWith(prefix) ?? false

// Hide the fake app when the real one is loaded
onMounted(() => {
  document.getElementById('fake-app')?.remove()
})

const { t } = useI18n()

const store = useStore()
const uploads = useUploadsStore()
const homeRouteName = computed(() => store.state.auth.authenticated ? 'library.index' : 'index')

const customLogoUrl = computed(() => {
  const url = store.state.instance.settings.ui.logo.value
  return url ? store.getters['instance/absoluteUrl'](url) : undefined
})

// Pods where the "API Requires authentication" setting is off allow anonymous browsing
const anonymousCanListen = computed(() =>
  store.state.instance.nodeinfo?.metadata?.features?.includes('anonymousCanListen') ?? false
)

const nodeinfo = computed(() => store.state.instance.nodeinfo)
const terms = useMarkdown(() => nodeinfo.value?.metadata?.terms ?? '')

const isOpen = ref(false)

// Admin notifications

const moderationNotifications = computed(() =>
  store.state.ui.notifications.pendingReviewEdits
  + store.state.ui.notifications.pendingReviewReports
  + store.state.ui.notifications.pendingReviewRequests
)
</script>

<template>
  <Layout
    aside
    gap-12
    :class="[$style.sidebar, $style['sticky-content']]"
    v-bind="$attrs"
  >
    <Layout
      header
      flex
      no-gap
      style="justify-content: space-between; align-items: center; padding-inline: 8px; padding-top: 8px;"
    >
      <Link
        v-bind="to({ name: homeRouteName }, { ariaCurrentValue: 'false' })"
        class="color-default-ghost"
        :class="customLogoUrl ? 'shape-square' : 'shape-circle'"
        ui-block-size="40"
        :aria-label="t('components.Sidebar.link.home')"
      >
        <img
          v-if="customLogoUrl"
          :src="customLogoUrl"
          alt=""
          :class="$style['custom-logo']"
        >
        <Logo v-else />
      </Link>

      <Layout
        nav
        gap-16
        flex
        style="align-items: center;"
      >
        <Popover
          v-if="store.state.auth.availablePermissions.settings || store.state.auth.availablePermissions.moderation"
          v-model="isOpen"
        >
          <Button
            v-if="store.state.auth.availablePermissions.settings || store.state.auth.availablePermissions.moderation"
            :key="moderationNotifications"
            class="color-default-ghost shape-circle"
            ui-block-size="40"
            :aria-pressed="isOpen"
            @click="isOpen = !isOpen"
          >
            <Badge
              v-if="moderationNotifications > 0"
              ui-raised
            >
              {{ moderationNotifications }}
            </Badge>
            <Icon icon="bi:wrench" />
          </Button>
          <template #items>
            <PopoverItem
              v-if="store.state.auth.availablePermissions.library"
              :to="{ name: 'manage.library.edits', query: { q: 'is_approved:null' } }"
              icon="bi:music-note-beamed"
              :title="t('components.Sidebar.label.edits')"
            >
              {{ t('components.Sidebar.link.library') }}

              <Badge v-if="store.state.ui.notifications.pendingReviewEdits > 0">
                {{ store.state.ui.notifications.pendingReviewEdits }}
              </Badge>
            </PopoverItem>

            <PopoverItem
              v-if="store.state.auth.availablePermissions.moderation"
              :to="{ name: 'manage.moderation.reports.list', query: { q: 'resolved:no' } }"
              icon="bi:megaphone-fill"
              :title="t('components.Sidebar.label.moderation')"
            >
              {{ t('components.Sidebar.link.moderation') }}
              <Spacer grow />
              <div
                v-if="store.state.ui.notifications.pendingReviewReports + store.state.ui.notifications.pendingReviewRequests > 0"
                :title="t('components.Sidebar.label.reports')"
                style="
                  background: var(--fw-gray-400);
                  color: var(--fw-gray-800);
                  padding: 2px 7px;
                  border-radius: 10px;
                  font-size: 12px;
                "
              >
                {{ store.state.ui.notifications.pendingReviewReports + store.state.ui.notifications.pendingReviewRequests }}
              </div>
            </PopoverItem>

            <PopoverItem
              v-if="store.state.auth.availablePermissions.settings"
              :to="{ name: 'manage.users.users.list' }"
              icon="bi:people-fill"
            >
              {{ t('components.Sidebar.link.users') }}
            </PopoverItem>

            <PopoverItem
              v-if="store.state.auth.availablePermissions.settings"
              :to="{ path: '/manage/settings' }"
              icon="bi:wrench"
            >
              {{ t('components.Sidebar.link.settings') }}
            </PopoverItem>
          </template>
        </Popover>

        <Link
          v-if="store.state.auth.authenticated"
          class="color-default-ghost shape-circle"
          ui-block-size="40"
          v-bind="dialog.to('upload')"
        >
          <Icon icon="bi:upload" />
          <Transition>
            <div
              v-if="uploads.currentIndex < uploads.queue.length"
              :class="$style['upload-progress']"
            >
              <div :class="[$style.progress, $style.fake]" />
              <div
                :class="$style.progress"
                :style="{ maxWidth: `${uploads.progress}%` }"
              />
            </div>
          </Transition>
        </Link>

        <UserMenu />

        <div class="hide-on-desktop">
          <Button
            class="color-default-ghost shape-circle"
            ui-block-size="40"
            :aria-pressed="!isCollapsed"
            @click="isCollapsed = !isCollapsed"
          >
            <Icon icon="bi:list" style="font-size: 1.75em;" />
          </Button>
        </div>
      </Layout>
    </Layout>
    <Layout
      no-gap
      stack
      :class="[$style['menu-links'], isCollapsed && 'hide-on-mobile']"
    >
      <!-- The search input will live next to the search modal. It needs  -->
      <!-- <Input
        ref="globalSearchInput"
        v-model="searchParameter"
        autocomplete="search"
        type="search"
        icon="bi:search"
        :placeholder="t('components.audio.SearchBar.placeholder.search')"
      /> -->

      <SearchModal />

      <Spacer />

      <!-- Sign up, Log in -->
      <template v-if="!store.state.auth.authenticated">
        <Layout
          flex
          gap-16
        >
          <Link
            v-bind="to({ name: 'login' })"
            class="color-primary-solid shape-field-grow"
            ui-no-underline
          >
            <Icon icon="bi:box-arrow-in-right" />
            {{ t('components.common.UserMenu.link.login') }}
          </Link>
          <Link
            v-bind="to({ name: 'signup' })"
            class="color-default-solid shape-field-grow"
            ui-raised
            ui-no-underline
          >
            <Icon icon="bi:person-square" />
            {{ t('components.common.UserMenu.link.signup') }}
          </Link>
        </Layout>
        <Spacer grow />
      </template>

      <!-- Main menu -->
      <nav
        v-if="store.state.auth.authenticated || anonymousCanListen"
        style="display:grid;"
      >
        <Link
          v-bind="to({ name: 'library.index' }, { ariaCurrentValue: 'page' })"
          ui-no-underline
          class="color-default-ghost justify-start shape-field-full"
        >
          <Icon icon="bi:clock-history" />
          <span :class="$style['indicate-active']">
            {{ t('components.Sidebar.header.activity') }}
          </span>
        </Link>

        <Link
          v-bind="to({ name: 'library.channels.browse' }, { ariaCurrentValue: 'page' })"
          ui-no-underline
          class="color-default-ghost justify-start shape-field-full"
        >
          <Icon icon="bi:music-note-beamed" />
          <span :class="$style['indicate-active']">
            {{ t('components.Sidebar.link.channels') }}
          </span>
        </Link>

        <Link
          v-bind="to({ name: 'library.podcasts.browse' }, { ariaCurrentValue: 'page' })"
          ui-no-underline
          class="color-default-ghost justify-start shape-field-full"
        >
          <Icon icon="bi:mic" />
          <span :class="$style['indicate-active']">
            {{ t('components.Sidebar.link.podcasts') }}
          </span>
        </Link>

        <h2
          class="font-caption"
          :class="$style['section-heading']"
        >
          {{ t('components.Sidebar.link.library') }}
        </h2>

        <Link
          v-bind="to({ name: 'library.artists.browse' })"
          :aria-current="isSectionActive('library.artists') ? 'page' : 'false'"
          ui-no-underline
          class="color-default-ghost justify-start shape-field-full"
        >
          <Icon icon="bi:person-circle" />
          <span :class="$style['indicate-active']">
            {{ t('components.Sidebar.link.artists') }}
          </span>
        </Link>

        <Link
          v-bind="to({ name: 'library.albums.browse' })"
          :aria-current="isSectionActive('library.albums') ? 'page' : 'false'"
          ui-no-underline
          class="color-default-ghost justify-start shape-field-full"
        >
          <Icon icon="bi:disc" />
          <span :class="$style['indicate-active']">
            {{ t('components.Sidebar.link.albums') }}
          </span>
        </Link>

        <Link
          v-bind="to({ name: 'library.playlists.browse' })"
          :aria-current="isSectionActive('library.playlists') ? 'page' : 'false'"
          ui-no-underline
          class="color-default-ghost justify-start shape-field-full"
        >
          <Icon icon="bi:music-note-list" />
          <span :class="$style['indicate-active']">
            {{ t('components.Sidebar.link.playlists') }}
          </span>
        </Link>
        <Link
          v-bind="to({ name: 'library.radios.browse' })"
          :aria-current="isSectionActive('library.radios') ? 'page' : 'false'"
          ui-no-underline
          class="color-default-ghost justify-start shape-field-full"
        >
          <Icon icon="bi:boombox-fill" />
          <span :class="$style['indicate-active']">
            {{ t('components.Sidebar.link.radios') }}
          </span>
        </Link>
        <!-- Anonymous visitors get the muted look but a real link: the
             favorites route guard forwards them to login and back after.
             No aria-disabled — the link works, so it must not claim otherwise -->
        <Link
          v-bind="to({ name: 'favorites' }, { ariaCurrentValue: 'page' })"
          ui-no-underline
          class="color-default-ghost justify-start shape-field-full"
          :style="store.state.auth.authenticated ? undefined : { opacity: 0.5 }"
        >
          <Icon icon="bi:heart" />
          <span :class="$style['indicate-active']">
            {{ t('components.Sidebar.link.favorites') }}
          </span>
        </Link>
      </nav>
      <Spacer grow />
      <Layout
        nav
        flex
        style="justify-content: center"
      >
        <Link
          v-bind="to({ name: 'about' })"
        >
          {{ t('components.Sidebar.link.about') }}
        </Link>
        <Link
          v-if="terms"
          v-bind="dialog.to('terms')"
        >
          {{ t('components.Sidebar.link.policy') }}
        </Link>
        <Spacer shrink />
      </Layout>
    </Layout>
  </Layout>
  <TermsModal />
</template>

<style module>
.section-heading {
  color: color-mix(in oklab, currentcolor 50%, var(--border-color));
  margin: 0;
}

.custom-logo {
  max-width: 100%;
  max-height: 100%;
  aspect-ratio: 1;
  object-fit: contain;
}

.sidebar {
  &.sticky-content {
    overflow: auto;
    top: 0;

    .upload-progress {
      background: var(--fw-blue-500);
      position: absolute;
      left: 0;
      bottom: 2px;
      width: 100%;
      padding: 2px;

      &:global(.v-enter-active),
      &:global(.v-leave-active) {
        transition: transform 0.2s ease, opacity 0.2s ease;
      }

      &:global(.v-leave-to),
      &:global(.v-enter-from) {
        transform: translateY(0.5rem);
        opacity: 0;
      }

      > .progress {
        height: 0.25rem;
        width: 100%;
        transition: max-width 0.1s ease;
        background: var(--fw-gray-100);
        border-radius: 100vh;
        position: relative;

        &.fake {
          background: var(--fw-blue-700);
        }

        &:not(.fake) {
          position: absolute;
          inset: 2px;
        }
      }
    }

    .avatar {
      aspect-ratio: 1;
      background: var(--fw-beige-100);
      border-radius: 100%;

      text-decoration: none !important;
      color: var(--fw-gray-700);

      > img,
      > i {
        width: 100%;
        height: 100%;

        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 !important;
        border-radius: 100vh;
      }
    }

    > h3 {
      margin: 0;
      padding: 0 32px 8px;
      font-size: 14px;
      line-height: 1.2;
    }
    .menu-links {
      padding: 0 16px 72px;
      flex-grow: 1;
    }
  }

  :global(.hide-on-mobile) {
    max-height: 0px;
    min-height: 0px;
    overflow: hidden;
    transition: all .8s;
  }

  @media screen and (min-width: 1024px) {
    height: 100%;

    :global(.hide-on-desktop) {
      display: none !important;
    }

    :global(.hide-on-mobile) {
      max-height: 100dvh;
      pointer-events: unset;
    }

    &.sticky-content {
      position: sticky;
      max-height: 100dvh;
    }
  }
}

.indicate-active {
    text-decoration: none;
}

/* Links are ultra-bold when active */
[aria-current="page"] .indicate-active {
  font-weight: 1000;
  letter-spacing: -.025em;
}
</style>
